import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { FiSearch, FiEdit2, FiArrowLeft } from "react-icons/fi";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";
import PaginatedItems from "./pagination";
import EditModal from "../../common/editModal";
import { getAttorneyData, searchAttorneyCodes, updateDiscount } from "../../api/server";
import "./manage-attorney-codes.css";

const money = (value) => Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const ManageCouponCodes = () => {
  const [response, setResponse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [revision, setRevision] = useState(0);
  const [editModal, setEditModal] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    const request = query ? searchAttorneyCodes(pageNo, query) : getAttorneyData(pageNo);
    request.then((res) => {
      if (!current) return;
      setResponse(res.data.data.result);
      setTotal(res.data.data.total);
    }).catch((err) => {
      if (!current) return;
      setResponse([]);
      setTotal(0);
      setError(err.response?.data?.error || "Unable to load attorney codes. Please try again.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [pageNo, query, revision]);

  const clearSearch = () => { setSearch(""); setQuery(""); setPageNo(1); };
  const handleEdit = async () => {
    const discount = Number(discountValue);
    if (String(discountValue).trim() === "" || !Number.isFinite(discount) || discount < 0) {
      toast.error("Enter a valid discount of $0 or more.");
      return;
    }
    setSaving(true);
    try {
      await updateDiscount(editModal, discount);
      setEditModal("");
      setRevision((value) => value + 1);
      toast.success("Attorney discount updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.error || "Unable to update discount. Please try again.");
    } finally { setSaving(false); }
  };
  return (
    <div className="wrapper">
      <DashBoardSidebar />
      <div id="content" className="attorney-codes-content">
        <DashboardNavbar />
        <EditModal loading={saving} showModal={editModal} setEdit={setEditModal}
          handleSubmit={handleEdit} discountValue={discountValue} setDiscountValue={setDiscountValue} />
        <section className="manage-students attorney-codes-page">
          <header className="attorney-codes-heading">
            <h1>Manage Attorney Codes</h1>
            <Link to="/manageattorney" className="attorney-codes-back"><FiArrowLeft aria-hidden="true" /> Manage Attorneys</Link>
          </header>
          <div className="card attorney-codes-card">
            <div className="attorney-codes-toolbar">
              <form className="attorney-codes-search" role="search" onSubmit={(event) => {
                event.preventDefault(); setPageNo(1); setQuery(search.trim()); setRevision((value) => value + 1);
              }}>
                <label className="visually-hidden" htmlFor="attorney-code-search">Search by name, email, or code</label>
                <div className="attorney-codes-search-input">
                  <input id="attorney-code-search" type="search" placeholder="Name, email, or code" value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      if (!event.target.value.trim()) { setQuery(""); setPageNo(1); }
                    }} />
                  <button type="submit" className="attorney-codes-search-submit" aria-label="Search"><FiSearch aria-hidden="true" /></button>
                </div>
                {query && <button type="button" className="attorney-codes-clear" onClick={clearSearch}>Clear Search</button>}
              </form>
            </div>
            <div className="attorney-codes-table-wrap" aria-busy={loading} role="region" aria-label="Attorney codes table" tabIndex={0}>
              <table className="table attorney-codes-table">
                <colgroup><col className="attorney-codes-name-col" /><col /><col className="attorney-codes-code-col" /><col className="attorney-codes-discount-col" /><col className="attorney-codes-action-col" /></colgroup>
                <thead><tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Attorney Code</th><th scope="col">Discount</th><th scope="col">Action</th></tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan={5} className="attorney-codes-state"><ThreeDots height="36" width="64" color="#4f9e4b" ariaLabel="Loading attorney codes" wrapperStyle={{ justifyContent: "center" }} /></td></tr>
                    : error ? <tr><td colSpan={5} className="attorney-codes-state"><p role="alert">{error}</p><button type="button" className="attorney-codes-search-button" onClick={() => setRevision((value) => value + 1)}>Try again</button></td></tr>
                    : response.length ? response.map((attorney) => <tr key={attorney.id}>
                      <th scope="row">{attorney.atty_name || "—"}</th><td>{attorney.atty_email || "—"}</td>
                      <td><span className="attorney-code-badge">{attorney.code || "—"}</span></td>
                      <td><span className={`attorney-discount${Number(attorney.discount) > 0 ? " has-discount" : ""}`}>{money(attorney.discount)}</span></td>
                      <td><button type="button" className="attorney-codes-edit" aria-label={`Edit discount for ${attorney.atty_name || attorney.code}`} onClick={() => {
                        setEditModal(attorney.id); setDiscountValue(attorney.discount ?? 0);
                      }}><FiEdit2 aria-hidden="true" /><span>Edit</span></button></td>
                    </tr>) : <tr><td colSpan={5} className="attorney-codes-state"><h3>No attorney codes found</h3></td></tr>}
                </tbody>
              </table>
            </div>
            {!loading && !error && <footer className="attorney-codes-footer" aria-live="polite">
              <span>{total ? `Showing ${(pageNo - 1) * 50 + 1}–${Math.min(pageNo * 50, total)} of ${total}` : "0 results"}</span>
              {total > 50 && <PaginatedItems responsive compactMarginPagesDisplayed={2} total={total} pageNo={pageNo} setPageNo={setPageNo} />}
            </footer>}
          </div>
        </section>
      </div>
    </div>
  );
};
export default ManageCouponCodes;
